import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Bundle-Auswahl.
 *
 * Klick auf eine Karte setzt die Menge im Mengenfeld des Themes und schreibt den
 * Gesamtpreis in den Kaufbutton. Der Preis wird nur ANGEZEIGT — abgerechnet wird
 * über die automatischen Rabatte, damit Anzeige und Checkout nicht auseinander
 * laufen können.
 */
class BundleAuswahl extends Component {
  #urspruenglicherKnopftext = null;

  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId;
    this.knopftext = this.dataset.knopftext || "";
    this.karten = Array.from(this.querySelectorAll(".bundle__karte"));

    // Auch von außen geänderte Mengen sollen die Karten mitnehmen: Die
    // Plus/Minus-Knöpfe des Themes feuern kein input-Event, sondern
    // quantity-selector:update (assets/events.js).
    this.onMengeGeaendert = () => this.#ausMengeUebernehmen();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onMengeGeaendert);
    this.#section.addEventListener("input", this.onMengeGeaendert);

    this.#ausMengeUebernehmen();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onMengeGeaendert);
    this.#section.removeEventListener("input", this.onMengeGeaendert);
    this.#knopftextZuruecksetzen();
  }

  get #section() {
    return document.getElementById(`shopify-section-${this.sectionId}`) || document;
  }

  get #mengenfeld() {
    return this.#section.querySelector("input.quantity-input");
  }

  /* ------------------------------------------------------------ Auswahl */

  stufeWaehlen(event) {
    // event.target, nicht currentTarget — das Theme reicht die Methode über
    // einen globalen Listener nach (assets/component.js).
    const karte = event.target.closest(".bundle__karte");
    if (!karte) return;

    const menge = parseInt(karte.dataset.menge, 10);
    const feld = this.#mengenfeld;

    if (feld) {
      feld.value = menge;
      // Beide Wege: input für Zuhörer am Feld, das Theme-Ereignis für alles,
      // was auf den Mengenwähler hört (z. B. die Staffelpreis-Tabelle).
      feld.dispatchEvent(new Event("input", { bubbles: true }));
      feld.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.#markieren(menge);
    this.#knopftextSetzen(menge, parseInt(karte.dataset.stueckpreis, 10));
  }

  /** Übernimmt eine anderweitig gesetzte Menge in die Kartenauswahl. */
  #ausMengeUebernehmen() {
    const feld = this.#mengenfeld;
    const menge = feld ? parseInt(feld.value, 10) : 1;
    if (!Number.isFinite(menge)) return;

    const passend = this.#kartefuer(menge);
    this.#markieren(passend ? parseInt(passend.dataset.menge, 10) : null);
    this.#knopftextSetzen(menge, passend ? parseInt(passend.dataset.stueckpreis, 10) : null);
  }

  /** Die Karte, deren Stufe die Menge erreicht (höchste passende). */
  #kartefuer(menge) {
    let treffer = null;
    for (const karte of this.karten) {
      if (menge >= parseInt(karte.dataset.menge, 10)) treffer = karte;
    }
    return treffer;
  }

  #markieren(menge) {
    for (const karte of this.karten) {
      const aktiv = parseInt(karte.dataset.menge, 10) === menge;
      karte.classList.toggle("is-gewaehlt", aktiv);
      karte.setAttribute("aria-checked", aktiv ? "true" : "false");
      if (aktiv) {
        const anzeige = this.refs.gewaehlt;
        if (anzeige) anzeige.textContent = karte.querySelector(".bundle__menge")?.textContent.trim() || "";
      }
    }
  }

  /* -------------------------------------------------- Preis im Kaufbutton */

  #knopftextSetzen(menge, stueckpreis) {
    if (!this.knopftext || !Number.isFinite(stueckpreis)) return;

    const ziel = this.#section.querySelector(".add-to-cart-text__content");
    if (!ziel) return;

    if (this.#urspruenglicherKnopftext === null) {
      this.#urspruenglicherKnopftext = ziel.textContent;
    }

    const summe = (stueckpreis * menge) / 100;
    ziel.textContent = this.knopftext.replace("[summe]", this.#geld(summe));
  }

  #knopftextZuruecksetzen() {
    if (this.#urspruenglicherKnopftext === null) return;
    const ziel = this.#section.querySelector(".add-to-cart-text__content");
    if (ziel) ziel.textContent = this.#urspruenglicherKnopftext;
  }

  /** Formatiert wie der Shop — Locale und Währung kommen aus dem Dokument. */
  #geld(betrag) {
    const sprache = document.documentElement.lang || "de";
    const waehrung = window.Shopify?.currency?.active || "EUR";
    try {
      return new Intl.NumberFormat(sprache, { style: "currency", currency: waehrung }).format(betrag);
    } catch {
      return betrag.toFixed(2).replace(".", ",") + " €";
    }
  }
}

if (!customElements.get("bundle-auswahl")) {
  customElements.define("bundle-auswahl", BundleAuswahl);
}
